/**
 * Crypto utilities with fallback for browsers without crypto.subtle
 */

/**
 * Generate SHA-256 hash with fallback
 */
export async function sha256(text: string): Promise<string> {
  // Check if crypto.subtle is available (requires HTTPS in Safari)
  if (typeof crypto !== 'undefined' && crypto.subtle) {
    try {
      const encoder = new TextEncoder()
      const data = encoder.encode(text)
      const hashBuffer = await crypto.subtle.digest('SHA-256', data)
      const hashArray = Array.from(new Uint8Array(hashBuffer))
      return hashArray.map(b => b.toString(16).padStart(2, '0')).join('')
    } catch (error) {
      console.warn('crypto.subtle.digest failed, using fallback:', error)
      return sha256Fallback(text)
    }
  } else {
    // Use fallback for environments without crypto.subtle
    return sha256Fallback(text)
  }
}

/**
 * Simple hash fallback for environments without crypto.subtle
 * This is NOT cryptographically secure but sufficient for fingerprinting
 */
function sha256Fallback(text: string): string {
  let hash = 0
  for (let i = 0; i < text.length; i++) {
    const char = text.charCodeAt(i)
    hash = ((hash << 5) - hash) + char
    hash = hash & hash // Convert to 32-bit integer
  }
  
  // Convert to hex string
  const hexHash = Math.abs(hash).toString(16).padStart(8, '0')
  
  // Pad to 64 characters to match SHA-256 length
  return hexHash.repeat(8).substring(0, 64)
}

/**
 * Generate a random UUID
 */
export function generateUUID(): string {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID()
  }
  
  // Fallback UUID generation
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
    const r = Math.random() * 16 | 0
    const v = c === 'x' ? r : (r & 0x3 | 0x8)
    return v.toString(16)
  })
}

/**
 * Check if crypto APIs are available
 */
export function isCryptoAvailable(): boolean {
  return typeof crypto !== 'undefined' && crypto.subtle !== undefined
}

/**
 * Get crypto availability info
 */
export function getCryptoInfo(): {
  available: boolean
  hasSubtle: boolean
  hasRandomUUID: boolean
  isSecureContext: boolean
} {
  return {
    available: typeof crypto !== 'undefined',
    hasSubtle: typeof crypto !== 'undefined' && crypto.subtle !== undefined,
    hasRandomUUID: typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function',
    isSecureContext: typeof window !== 'undefined' && window.isSecureContext === true
  }
}